import React, { Component } from 'react';
import { StyleSheet, View, ImageBackground, TouchableOpacity } from 'react-native';
import { observer } from 'mobx-react';

import Icon from '../shared/Icon';
import Text from '../shared/Text';
import { NameFrame } from '../shared/Modal';
import IconButton from '../shared/IconButton';

import { moveToBuilding } from '../BuildingScreen';

import WarriorsInfo from './WarriorsInfo';

import appStore from '../../stores/app';
import heroStore from '../../stores/hero';

import { arrayContains, getMapMargin, getIsland } from '../../utils/utils';
import { getBuildingByCoords } from '../../utils/buildingUtils';

import { islandImageRequire } from '../../lib/imagesRequire';

import { t, getLocalValue } from '../../utils/appUtils';

import { IS_WEB } from '../../lib/appConstants';

import { ISLAND_MOVE_TIME } from '../../lib/config';
import { isFull } from '../../utils/warriorUtils';
import {
  PADDING,
  HP_HEIGHT,
  TOUCHABLE,
  WINDOW_WIDTH,
  WINDOW_HEIGHT,
  GOLDEN200,
  MARGIN_10,
  MARGIN_15,
  pOrT,
} from '../styleConstants';

import Image from '../shared/Image';

const DELTA = pOrT(1.4, 1);

const SQUARE_WIDTH = pOrT(32 / DELTA, 32);

const MAX_HEIGHT = IS_WEB ? 650 : WINDOW_HEIGHT;

export const BG_OFFSET = 42;

const ISLAND_WIDTH = 1216 / DELTA;
const AREA_WIDTH = WINDOW_WIDTH - PADDING * 2;
const AREA_HEIGHT = MAX_HEIGHT - HP_HEIGHT - PADDING * 3;

export const getScreenDimension = () => {
  return {
    width: WINDOW_WIDTH - PADDING * 2,
    height: WINDOW_HEIGHT - PADDING * 2,
  };
};

const ISLAND_DIMENSIONS = {
  width: ISLAND_WIDTH / SQUARE_WIDTH,
  height: ISLAND_WIDTH / SQUARE_WIDTH,
};

export const MAP_DIMENSIONS = {
  width: AREA_WIDTH > ISLAND_WIDTH ? ISLAND_WIDTH : AREA_WIDTH,
  height: AREA_HEIGHT > ISLAND_WIDTH ? ISLAND_WIDTH : AREA_HEIGHT,
};

export const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  positionInfo: {
    width: 200,
    height: 50,
    marginLeft: -100,
    position: 'absolute',
    left: '50%',
    alignItems: 'center',
  },
});

export function checkIsFull() {
  if (isFull(heroStore.hero)) {
    appStore.showMessage({ type: 'error', text: t('hero.fullInventory') });
    return false;
  }
  return true;
}

type State = {
  moveTime: number;
};

type PaperBackgroundProps = {
  width: number;
  height: number;
  style?: any;
  noPaper?: boolean;
};

export const PaperBackground = ({ width, height, style = {}, noPaper }: PaperBackgroundProps) => (
  <ImageBackground
    style={{
      width,
      height,
      position: 'absolute',
      marginLeft: WINDOW_WIDTH > width + 30 ? (WINDOW_WIDTH - width - 30) / 2 : (null as any),
      ...style,
    }}
    resizeMode="stretch"
    source={require('../../assets/images/bigBar2.png')}
  >
    {!noPaper && (
      <Image
        style={{
          marginLeft: 15,
          marginTop: 15,
          width: width - 30,
          height: height - 30,
          position: 'absolute',
        }}
        resizeMode="stretch"
        source={require('../../assets/images/paper2.png')}
      />
    )}
  </ImageBackground>
);

@observer
export default class IslandScreen extends Component<{}, State> {
  state = {
    moveTime: 0,
    disabledCoords: [] as number[][],
  };

  moveInterval: any = null;

  onMove = (x: number, y: number) => {
    if (this.moveInterval) return;
    if (!checkIsFull()) return;
    const island = getIsland(appStore.initData.islands, heroStore.hero.location.island);
    if (arrayContains(island.disabledCoordinates || [], [x, y]) !== -1) return;

    let counter = ISLAND_MOVE_TIME;

    this.setState({
      moveTime: counter,
    });

    this.moveInterval = setInterval(async () => {
      counter -= 0.5;

      this.setState({
        moveTime: counter,
      });

      if (counter <= 0) {
        clearInterval(this.moveInterval);
        this.moveInterval = null;
        await heroStore.moveOnIsland(x, y);
      }
    }, 500);
  };

  onCancelMove = () => {
    clearInterval(this.moveInterval);
    this.moveInterval = null;
    this.setState({
      moveTime: 0,
    });
  };

  renderPositionInfo() {
    const { coordinateX, coordinateY, island } = heroStore.hero.location;
    const { moveTime } = this.state;

    const building = getBuildingByCoords(
      appStore.initData.buildings,
      island,
      coordinateX,
      coordinateY,
    );
    return (
      <View style={styles.positionInfo}>
        <NameFrame
          // fullScreen
          center={false}
          title={
            <View
              style={{
                width: pOrT(200, 300),
                alignItems: 'center',
              }}
            >
              {moveTime || !building ? (
                <Text
                  bold
                  style={{
                    marginTop: pOrT(MARGIN_10, MARGIN_15),
                    color: GOLDEN200,
                  }}
                >{`${t('island.position')} ${coordinateX}:${coordinateY}`}</Text>
              ) : null}
              {building && !moveTime ? (
                <View
                  style={{
                    justifyContent: 'center',
                    height: pOrT(50, 75),
                  }}
                >
                  <TouchableOpacity
                    hitSlop={{
                      top: TOUCHABLE,
                      bottom: TOUCHABLE,
                      left: TOUCHABLE,
                      right: TOUCHABLE,
                    }}
                    onPress={() => {
                      moveToBuilding(building.id);
                    }}
                  >
                    <Text
                      bold
                      style={{
                        fontSize: pOrT(12, 16),
                        color: GOLDEN200,
                      }}
                    >
                      {getLocalValue(building)}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : null}
              {moveTime ? <Text style={{ color: GOLDEN200 }}>{t('island.moving')}</Text> : null}
            </View>
          }
        />
      </View>
    );
  }

  render() {
    const { hero } = heroStore;
    const { coordinateX, coordinateY } = hero.location;

    const island = getIsland(appStore.initData.islands, hero.location.island);

    const mapMargin = getMapMargin(
      coordinateX,
      coordinateY,
      {
        width: (MAP_DIMENSIONS.width - BG_OFFSET) / SQUARE_WIDTH,
        height: (MAP_DIMENSIONS.height - BG_OFFSET) / SQUARE_WIDTH,
      },
      ISLAND_DIMENSIONS,
    );

    const mapOffset = {
      top: Math.floor(mapMargin.top) * SQUARE_WIDTH,
      left: Math.floor(mapMargin.left) * SQUARE_WIDTH,
    };

    const squares = [];

    const cY = coordinateY;
    const cX = coordinateX;

    /*
    const filledCoords = fill(island.disabledCoordinates, [26, 16]);
    */

    /* eslint-disable no-continue */
    const lineY = /* ISLAND_DIMENSIONS.height */ cY - 1 + 3;
    const lineX = /* ISLAND_DIMENSIONS.width */ cX - 1 + 3;
    for (let x = /* 0 */ cX - 1; x < lineX; x += 1) {
      if (x < 0 || x > ISLAND_DIMENSIONS.width) continue;
      for (let y = /* 0 */ cY - 1; y < lineY; y += 1) {
        if (y < 0 || y > ISLAND_DIMENSIONS.height) continue;
        if (x === cX && y === cY) continue;
        if (
          (x === cX - 1 && y === cY - 1) ||
          (x === cX + 1 && y === cY - 1) ||
          (x === cX - 1 && y === cY + 1) ||
          (x === cX + 1 && y === cY + 1)
        ) {
          continue;
        }

        const rotDeg =
          x === cX - 1 && y === cY
            ? 270
            : x === cX + 1 && y === cY
            ? 90
            : x === cX && y === cY + 1
            ? 0
            : 180;
        /* eslint-disable */
        squares.push(
          <View
            key={`${x}-${y}`}
            style={{
              position: 'absolute',
              backgroundColor: 'transparent',
              zIndex: 99,
              top: y * SQUARE_WIDTH - mapOffset.top,
              left: x * SQUARE_WIDTH - mapOffset.left,
              transform: [
                {
                  rotate: `-${rotDeg}deg`,
                },
              ],
            }}
          >
            <IconButton
              onPress={() => this.onMove(x, y)}
              // onPress={() => { 
          //   this.setState((state) => {
          //     const idx = arrayContains(state.disabledCoords, [x, y]);
          //     if (idx !== -1) {
          //       return { disabledCoords: state.disabledCoords.filter((_, i) => i !== idx) };
          //     }
          //     return { disabledCoords: state.disabledCoords.concat([[x, y]]) };
          //   });
          // }}
              source={require('../../assets/images/arrowDown.png')}
              iconStyle={{
                width: pOrT(18, 24),
                height: pOrT(18, 24),
              }}
              size={pOrT(22, 28)}
              name="arrowUp"
              color="#00000078"
              title={t('hero.move')}
            />
          </View>,
        );
        /* eslint-enable */

        // squares.push(<TouchableOpacity
        //   key={`${x}-${y}`}
        //   style={{
        //     backgroundColor: 'black',
        //     backgroundColor: arrayContains(filledCoords, [x, y]) !== -1 ? 'red' : 'transparent',
        //     opacity: 0.2,
        //     position: 'absolute',
        //     top: y * SQUARE_WIDTH - mapOffset.top,
        //     left: x * SQUARE_WIDTH - mapOffset.left,
        //     width: SQUARE_WIDTH,
        //     height: SQUARE_WIDTH,
        //   }}
        //   onPress={() => this.onMove(x, y)}
        // />);
      }
    }
    /* eslint-enable no-continue */

    return (
      <View style={styles.container}>
        <PaperBackground width={MAP_DIMENSIONS.width} height={MAP_DIMENSIONS.height} />
        <View
          style={{
            height: MAP_DIMENSIONS.height - BG_OFFSET,
            width: MAP_DIMENSIONS.width - BG_OFFSET,
            overflow: 'hidden',
            marginLeft: -((MAP_DIMENSIONS.width - BG_OFFSET) / 2),
            marginTop: 20,
            position: 'absolute',
            left: '50%',
          }}
        >
          <Image
            style={{
              marginTop: -mapOffset.top,
              marginLeft: -mapOffset.left,
              width: ISLAND_WIDTH,
              height: ISLAND_WIDTH,
            }}
            source={islandImageRequire(island.image)}
          />
          {squares}
          <Icon
            size={pOrT(26, 32)}
            name="person"
            source={require('../../assets/images/hero.png')}
            style={{
              width: pOrT(28, 32),
              height: pOrT(28, 32),
              position: 'absolute',
              top: coordinateY * SQUARE_WIDTH - mapOffset.top - 4,
              left: coordinateX * SQUARE_WIDTH - mapOffset.left - 6,
              backgroundColor: 'transparent',
            }}
          />
        </View>
        {this.renderPositionInfo()}
        <WarriorsInfo />
      </View>
    );
  }
}